// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Mission Manager
 * 
 * Handles mission generation, tracking, completion, and rewards
 */

import { 
  MISSION_TEMPLATES, 
  MISSION_TYPES, 
  MISSION_DIFFICULTY, 
  MISSION_STATUS 
} from '../../data/missionTemplates';

class MissionManager {
  constructor() {
    this.activeMissions = [];
    this.completedMissions = [];
    this.availableMissions = [];
    this.missionIdCounter = 1;
  }

  /**
   * Generate a random mission
   */
  generateMission(currentSystem, allSystems, difficulty = 'MEDIUM') {
    const templates = Object.values(MISSION_TEMPLATES);
    const template = templates[Math.floor(Math.random() * templates.length)];
    
    const difficultyData = MISSION_DIFFICULTY[difficulty];
    const mission = {
      id: `mission_${this.missionIdCounter++}`,
      type: template.type,
      title: template.title,
      description: this.generateDescription(template, currentSystem, allSystems),
      objectives: this.generateObjectives(template, currentSystem, allSystems),
      reward: Math.floor(template.baseReward * difficultyData.multiplier),
      reputation: Math.floor(template.baseReputation * difficultyData.multiplier),
      timeLimit: template.baseTime,
      timeRemaining: template.baseTime,
      difficulty: difficulty,
      difficultyColor: difficultyData.color,
      status: MISSION_STATUS.AVAILABLE,
      origin: currentSystem,
      createdAt: Date.now(),
      template: template
    };

    // Add type-specific data
      // eslint-disable-next-line default-case
    switch (template.type) {
      case MISSION_TYPES.DELIVERY:
        mission.cargo = this.selectRandom(template.cargoTypes);
        mission.destination = this.selectDestination(currentSystem, allSystems);
        break;
      case MISSION_TYPES.COMBAT:
        mission.location = this.selectLocation(currentSystem, allSystems);
        mission.enemyCount = this.selectRandom(template.enemyCounts || [3]);
        break;
      case MISSION_TYPES.EXPLORATION:
        mission.targetSystem = this.selectDestination(currentSystem, allSystems);
        mission.planet = `Planet ${String.fromCharCode(65 + Math.floor(Math.random() * 8))}`;
        break;
      case MISSION_TYPES.RESCUE:
        mission.location = this.selectLocation(currentSystem, allSystems);
        break;
      case MISSION_TYPES.ESCORT:
        mission.origin = currentSystem;
        mission.destination = this.selectDestination(currentSystem, allSystems);
        break;
    }

    return mission;
  }

  /**
   * Generate mission description with placeholders filled
   */
  generateDescription(template, currentSystem, allSystems) {
    const descriptions = template.descriptions;
    let description = this.selectRandom(descriptions);
    
    // Replace placeholders
    description = description.replace('{cargo}', this.selectRandom(template.cargoTypes || ['Cargo']));
    description = description.replace('{destination}', this.selectDestination(currentSystem, allSystems));
    description = description.replace('{location}', this.selectLocation(currentSystem, allSystems));
    description = description.replace('{system}', this.selectDestination(currentSystem, allSystems));
    description = description.replace('{station}', `${this.selectDestination(currentSystem, allSystems)} Station`);
    description = description.replace('{planet}', `Planet ${String.fromCharCode(65 + Math.floor(Math.random() * 8))}`);
    description = description.replace('{count}', Math.floor(Math.random() * 3) + 2);
    description = description.replace('{origin}', currentSystem);
    
    return description;
  }

  /**
   * Generate objectives with placeholders filled
   */
  generateObjectives(template, currentSystem, allSystems) {
    return template.objectives.map(obj => {
      let objective = obj;
      objective = objective.replace('{destination}', this.selectDestination(currentSystem, allSystems));
      objective = objective.replace('{location}', this.selectLocation(currentSystem, allSystems));
      objective = objective.replace('{system}', this.selectDestination(currentSystem, allSystems));
      objective = objective.replace('{station}', `${this.selectDestination(currentSystem, allSystems)} Station`);
      objective = objective.replace('{planet}', `Planet ${String.fromCharCode(65 + Math.floor(Math.random() * 8))}`);
      objective = objective.replace('{count}', Math.floor(Math.random() * 3) + 2);
      objective = objective.replace('{origin}', currentSystem);
      return objective;
    });
  }

  /**
   * Select random destination system (not current)
   */
  selectDestination(currentSystem, allSystems) {
    const otherSystems = allSystems.filter(s => s !== currentSystem);
    return this.selectRandom(otherSystems) || 'Unknown System';
  }

  /**
   * Select random location
   */
  selectLocation(currentSystem, allSystems) {
    const locations = [
      `${currentSystem} Sector`,
      `Near ${currentSystem}`,
      `${this.selectDestination(currentSystem, allSystems)} Region`,
      'Asteroid Belt',
      'Nebula Zone',
      'Deep Space'
    ];
    return this.selectRandom(locations);
  }

  /**
   * Select random item from array
   */
  selectRandom(array) {
    return array[Math.floor(Math.random() * array.length)];
  }

  /**
   * Generate multiple missions for a station
   */
  generateMissionBoard(currentSystem, allSystems, count = 5) {
    const missions = [];
    const difficulties = ['EASY', 'EASY', 'MEDIUM', 'MEDIUM', 'HARD', 'EXTREME'];
    
    for (let i = 0; i < count; i++) {
      const difficulty = this.selectRandom(difficulties);
      const mission = this.generateMission(currentSystem, allSystems, difficulty);
      missions.push(mission);
    }
    
    // Sort missions by difficulty: EASY -> MEDIUM -> HARD -> EXTREME
    const difficultyOrder = { 'EASY': 1, 'MEDIUM': 2, 'HARD': 3, 'EXTREME': 4 };
    missions.sort((a, b) => difficultyOrder[a.difficulty] - difficultyOrder[b.difficulty]);

    this.availableMissions = missions;
    return missions;
  }

  /**
   * Accept a mission
   */
  acceptMission(missionId) {
    const mission = this.availableMissions.find(m => m.id === missionId);
    if (!mission) return null;

    mission.status = MISSION_STATUS.ACTIVE;
    mission.acceptedAt = Date.now();
    
    this.activeMissions.push(mission);
    this.availableMissions = this.availableMissions.filter(m => m.id !== missionId);
    
    return mission;
  }

  /**
   * Complete a mission
   */
  completeMission(missionId) {
    const mission = this.activeMissions.find(m => m.id === missionId);
    if (!mission) return null;

    mission.status = MISSION_STATUS.COMPLETED;
    mission.completedAt = Date.now();
    
    this.completedMissions.push(mission);
    this.activeMissions = this.activeMissions.filter(m => m.id !== missionId);
    
    return {
      mission,
      rewards: {
        credits: mission.reward,
        reputation: mission.reputation
      }
    };
  }

  /**
   * Fail a mission
   */
  failMission(missionId, reason = 'Mission failed') {
    const mission = this.activeMissions.find(m => m.id === missionId);
    if (!mission) return null;

    mission.status = MISSION_STATUS.FAILED;
    mission.failedAt = Date.now();
    mission.failReason = reason;
    
    this.completedMissions.push(mission);
    this.activeMissions = this.activeMissions.filter(m => m.id !== missionId);
    
    return mission;
  }

  /**
   * Update mission timers
   */
  updateMissions(deltaTime) {
    this.activeMissions.forEach(mission => {
      if (mission.timeLimit) {
        mission.timeRemaining -= deltaTime;
        
        if (mission.timeRemaining <= 0) {
          this.failMission(mission.id, 'Time expired');
        }
      }
    });
  }

  /**
   * Get active missions
   */
  getActiveMissions() {
    return this.activeMissions;
  }

  /**
   * Get available missions
   */
  getAvailableMissions() {
    return this.availableMissions;
  }

  /**
   * Get completed missions
   */
  getCompletedMissions() {
    return this.completedMissions;
  }

  /**
   * Get mission by ID
   */
  getMission(missionId) {
    return [...this.activeMissions, ...this.availableMissions, ...this.completedMissions]
      .find(m => m.id === missionId);
  }

  /**
   * Format time remaining
   */
  formatTimeRemaining(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
  /**
   * Check if player has arrived at delivery destination
   */
  checkDeliveryCompletion(currentSystemName) {
    const deliveryMissions = this.activeMissions.filter(m => m.type === 'delivery');
    const completed = [];
    
    deliveryMissions.forEach(mission => {
      if (mission.destination === currentSystemName) {
        const result = this.completeMission(mission.id);
        if (result) {
          completed.push(result);
        }
      }
    });
    
    return completed;
  }

  /**
   * Track combat kills for missions
   */
  recordCombatKill() {
    const combatMissions = this.activeMissions.filter(m => m.type === 'combat');
    const completed = [];
    
    combatMissions.forEach(mission => {
      mission.killCount = (mission.killCount || 0) + 1;
      
      if (mission.killCount >= mission.enemyCount) {
        const result = this.completeMission(mission.id);
        if (result) {
          completed.push(result);
        }
      }
    });
    
    return completed;
  }

  /**
   * Track exploration scans for missions
   */
  recordScan(systemName) {
    const explorationMissions = this.activeMissions.filter(m => m.type === 'exploration');
    const completed = [];
    
    explorationMissions.forEach(mission => {
      if (mission.targetSystem === systemName) {
        const result = this.completeMission(mission.id);
        if (result) {
          completed.push(result);
        }
      }
    });
    
    return completed;
  }

}

// Singleton instance
const missionManager = new MissionManager();
export default missionManager;
